<template>

  <div class="active-delivery-stepper-page">

    <!-- Fixed Action Bar -->
    <v-toolbar flat height="80" style="border-radius:4px;" class="app-fixed-toolbar delivery-toolbar">
      <v-btn plain icon color="primary" @click="$root.goToLastPage">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <route-leaf-logo width="100px" />
      <v-spacer />
      <!-- <v-tooltip bottom :nudge-top="10" v-if="routeToEdit && !showStartButton && !isCanceled && !isCompleted">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon plain color="primary" v-bind="attrs" v-on="on" @click="scrollToActiveStep">
            <v-icon small>mdi-arrow-collapse-down</v-icon>
            <span class="active-step-button-text">{{ getActiveStep() }}</span>
          </v-btn>
        </template>
        <div>Go to active step</div>
      </v-tooltip> -->
      <v-chip
          v-if="routeToEdit && isTestRoute"
          label
          color="orange darken-4"
          class="py-5"
          style="font-weight:500;">
        <span v-if="isMobileScreen">TEST ROUTE</span>
        <span v-else>THIS IS A TEST ROUTE</span>
      </v-chip>
      <v-spacer />
      <v-btn
          outlined
          color="primary"
          class="mr-2"
          @click="openRouteManager">
        <v-icon :style="{ fontSize: isMobileScreen ? '24px' : '20px' }">mdi-cog-outline</v-icon>
        <span style="padding-left:6px;" v-if="!isMobileScreen">Manage Route</span>
      </v-btn>
    </v-toolbar>

    <!-- Main Content -->
    <div class="main-content">
      <v-container fluid class="pa-0 ma-0">
        <!-- (data loading spinner) -->
        <v-container class="d-flex flex-column align-center justify-center" v-if="isDataLoading">
          <v-progress-circular indeterminate :size="50" style="margin-top:30px" color="primary" />
        </v-container>

        <div v-else-if="routeToEdit">
          <!------------------------------->
          <!-- Delivery canceled message -->
          <!------------------------------->
          <v-container fluid class="mt-6 d-flex flex-column align-center justify-center" v-if="isCanceled">
            <div style="font-size:1.5em">Delivery has been canceled</div>
            <div style="font-size:1.3em" class="grey--text mt-2">{{ formatDateTime(routeToEdit.canceled_at) }}</div>

            <div class="mt-10 mb-6">
              <v-btn color="primary" @click="goToMyDeliveriesPage">
                Upcoming deliveries
              </v-btn>
            </div>
          </v-container>

          <!------------------------------------------->
          <!-- Delivery Stepper UI (active delivery) -->
          <!------------------------------------------->
          <template v-else-if="!isCompleted">
            <!-- Start Button -->
            <v-container fluid class="d-flex flex-column align-center justify-center" v-if="showStartButton">
              <v-btn
                  x-large
                  color="primary darken-2"
                  class="btn-start-delivery"
                  @click="checkDriver">
                <span v-if="isTestRoute">Start Test Route</span>
                <span v-else>Start</span>
              </v-btn>
            </v-container>

            <!-- Driver Change Dialog -->
            <v-dialog persistent max-width="350" v-model="isDriverChangeDialogOpen">
              <v-card class="dialog-driver-change">
                <v-card-text style="font-size:17px;" class="pt-6 pb-2 d-flex flex-column align-center justify-center">
                  <div>This delivery was assigned to another driver:
                    <span style="font-style:italic;">{{ driver.display_name }}</span>
                  </div>
                  <div class="mt-4">Clicking "Start" will set the assigned driver to you:
                    <span style="font-style:italic;">{{ activeUser.display_name }}</span>
                  </div>
                </v-card-text>
                <v-card-actions class="pb-5 mt-3">
                  <v-btn text class="ml-8" @click="closeDriverChangeDialog">Cancel</v-btn>
                  <v-spacer />
                  <v-btn color="primary" class="mr-10" @click="changeAssignedDriver">Ok</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Delivery Stepper -->
            <v-container fluid class="pa-0 delivery-stepper-container">
              <delivery-stepper
                  ref="stepper"
                  :hq="vehicleLot"
                  :route="routeToEdit"
                  v-on:completed="completeDelivery" />
            </v-container>
          </template>

          <!------------------------------->
          <!-- Delivery complete message -->
          <!------------------------------->
          <v-container fluid class="mt-6 d-flex flex-column align-center justify-center" v-else>
            <div style="font-size:1.5em">Delivery Completed!</div>
            <div style="font-size:1.3em" class="grey--text mt-2">{{ formatDateTime(routeToEdit.completed_at) }}</div>

            <div class="mt-10 mb-6">
              <v-btn color="primary" @click="goToMyDeliveriesPage">
                Upcoming deliveries
              </v-btn>
            </div>

            <!-- Completed Delivery Stepper -->
            <v-container fluid class="delivery-stepper-container">
              <delivery-stepper
                  :hq="vehicleLot"
                  :route="routeToEdit" />
            </v-container>
          </v-container>
        </div>
      </v-container>
    </div>

    <v-dialog fullscreen v-model="isRouteManagerOpen">
      <v-card>
        <v-card-text class="d-flex align-center justify-center">
          <delivery-order-manager
              :route="routeToEdit"
              @rescheduled="reloadActiveDelivery"
              @canceled="reloadActiveDelivery"
              @reordered="reloadActiveDelivery"
              @close="closeRouteManager" />
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>

</template>

<script>
import RouteLeafLogo from '@/components/app/RouteLeafLogo'
import DeliveryStepper from '@/components/deliveries/DeliveryStepper'
import DeliveryOrderManager from '@/components/deliveries/DeliveryOrderManager'

export default {
  name: 'ActiveDeliveryStepperPage',

  components: {
    RouteLeafLogo,
    DeliveryStepper,
    DeliveryOrderManager
  },

  props: {
    routeId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isDataLoading: false,
      isDriverChangeDialogOpen: false,
      isDriverChangeProcessing: false,
      isRouteManagerOpen: false,
      isMobileScreen: window.innerWidth < 768
    }
  },

  computed: {
    // The active user
    activeUser () {
      return this.$store.getters['auth/activeUser']
    },
    isTestRoute () {
      return this.routeToEdit.is_test
    },
    // The assigned Driver (User)
    driver () {
      return (this.routeToEdit) ? this.routeToEdit.driver : {}
    },
    // The vehicle lot
    vehicleLot () {
      return (this.routeToEdit) ? this.routeToEdit.vehicle_lot : {}
    },
    // The active item being edited
    routeToEdit () {
      return this.$store.getters['sh/itemEditing']('activeDelivery')
    },
    isNew () {
      return (this.routeToEdit && this.routeToEdit.id === null)
    },
    isCanceled () {
      return this.routeToEdit.route_status === 'CANCELED'
    },
    isCompleted () {
      return this.routeToEdit.route_status === 'COMPLETED'
    },
    // The origin route that brought the user to this page
    origin () {
      return this.$store.getters['sh/itemEditingOrigin']('activeDelivery')
    },
    showStartButton () {
      return this.routeToEdit.route_status === 'DISPATCHED'
    }
  },

  mounted () {
    if (!this.routeToEdit) this.isDataLoading = true

    // Initialize viewport detection
    this.handleResize = () => {
      this.isMobileScreen = window.innerWidth < 768
    }
    window.addEventListener('resize', this.handleResize)
    this.isMobileScreen = window.innerWidth < 768 // set initial value
  },

  watch: {
    routeToEdit: function (item) {
      this.isDataLoading = false
    }
  },

  beforeDestroy () {
    // Clear editing registry
    this.$store.dispatch('sh/clearItemEditing', 'activeDelivery')

    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.format(date, 'MMMM D h:mm a z') : ''
    },

    showDriverChangeDialog () {
      this.isDriverChangeDialogOpen = true
    },

    closeDriverChangeDialog () {
      this.isDriverChangeDialogOpen = false
    },

    goToMyDeliveriesPage () {
      this.$root.goTo('delivery-list-dispatched')
    },

    getActiveStep () {
      const stepperUi = this.$refs.stepper
      return (stepperUi) ? stepperUi.getActiveStep() : 0
    },

    scrollToActiveStep () {
      const stepperUi = this.$refs.stepper
      if (stepperUi) stepperUi.scrollToActiveStep()
    },

    // Open route manager
    openRouteManager () {
      this.isRouteManagerOpen = true
    },

    // Close route manager
    closeRouteManager () {
      this.isRouteManagerOpen = false
    },

    // Check driver validation step (trigger change driver dialog if different driver)
    checkDriver () {
      if (this.activeUser.id !== this.routeToEdit.driver_user_id) {
        this.showDriverChangeDialog()
      } else {
        this.startDelivery()
      }
    },

    // Reload the active delivery
    async reloadActiveDelivery () {
      this.isDataLoading = true
      this.$parent.loadActiveDelivery()
    },

    // Change the assigned driver
    async changeAssignedDriver () {
      this.isDriverChangeProcessing = true

      try {
        // Perform driver change
        await this.$store.dispatch('sh/changeAssignedDriver', {
          routeId: this.routeId,
          newDriverUserId: this.activeUser.id
        })
        // Reload active delivery (route,orders,vehicle,driver)
        this.$parent.loadActiveDelivery()
        this.closeDriverChangeDialog()

        // Start delivery
        this.startDelivery()
      } catch (err) {
        console.log('Error changing assigned driver =>', err)
      }

      this.isDriverChangeProcessing = false
    },

    // Start delivery
    async startDelivery () {
      try {
        // Trigger start of route
        await this.$store.dispatch('sh/startDelivery', this.routeToEdit)
        // Start stepper UI
        const stepperUi = this.$refs.stepper
        if (stepperUi) stepperUi.activeStep = 1
      } catch (err) {
        console.log('Error starting delivery =>', err)
      }
    },

    // Complete delivery (currently a noop)
    completeDelivery () {
    }
  }
}
</script>

<style>
  .active-delivery-stepper-page {
    position: relative;
    height: 100vh;
    width: 100%;
    overflow: hidden;
  }

  .active-delivery-stepper-page .active-step-button-text {
    font-size: 1.04rem;
    font-weight: 500;
  }

  .active-delivery-stepper-page .main-content {
    position: absolute;
    top: 80px; /* Match toolbar height */
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch; /* For smooth scrolling on iOS */
  }

  .delivery-stepper-container {
    position: relative;
    height: 100%;
  }

  .dialog-driver-change.v-card {
    border: 1px solid #525252 !important;
  }

  .active-delivery-stepper-page .delivery-toolbar {
    margin-top: 6px;
    margin-right: 8px;
    margin-bottom: 4px;
    margin-left: 8px;
  }

  .active-delivery-stepper-page .delivery-toolbar .v-toolbar__content {
    background-color: #303030 !important;
    border-radius: 4px;
    border-bottom: 1px solid #000000;
  }

  .active-delivery-stepper-page .delivery-toolbar .toolbar-button {
    text-align: center;
  }
</style>
